import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import CustomFooter from '../components/Footer';
import { useParams, useNavigate } from 'react-router-dom';
import { getTeamDetails, editTeam, removeMember, requestCoach } from '../api/team_stuff';
import { Trash2 } from 'lucide-react';

const EditTeam = () => {
    const { teamname } = useParams();
    const navigate = useNavigate();
    const [formValues, setFormValues] = useState({ teamname: '', teamorg: '', coachName: '' });
    const [members, setMembers] = useState([]);
    const [coach, setCoach] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTeam = async () => {
            try {
                const data = await getTeamDetails(teamname);
                setFormValues({ teamname: data.name, teamorg: data.org, coachName: data.members.find(member => member.role === 'Coach')?.name || '' });
                setMembers(data.members.filter(member => member.role !== 'Coach'));
                setCoach(data.members.find(member => member.role === 'Coach'));
            } catch (err) {
                setError('Failed to load team details.');
            } finally {
                setLoading(false);
            }
        };
        fetchTeam();
    }, [teamname]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
    };

    const handleDelete = async (memberName) => {
        if (window.confirm(`Are you sure you want to remove ${memberName}?`)) {
            try {
                await removeMember(teamname, memberName);
                setMembers(prev => prev.filter(member => member.name !== memberName));
                if (coach && coach.name === memberName) {
                    setCoach(null);
                    setFormValues(prev => ({ ...prev, coachName: '' }));
                }
            } catch (error) {
                console.error('Error removing member:', error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await editTeam(formValues);
            if(response.status === "success"){
                navigate(`/edit_team/${encodeURIComponent(response.newTeamname)}`);
            }
        } catch (error) {
            console.error('Error updating team:', error);
            setError('Failed to update team. Please try again.');
        }
    };

    const coachRequest = async (e) => {
        e.preventDefault();
        try {
            await requestCoach(formValues);
        } catch (error) {
            console.error('Error updating team:', error);
            setError('Failed to update team. Please try again.');
        }
    };

    if (loading) return <p className="text-center mt-8 text-gray-600">Loading team details...</p>;
    if (error) return <p className="text-center mt-8 text-red-500">{error}</p>;

    return (
        <div className="bg-gray-100 min-h-screen flex flex-col">
            <Header />
            <div className="flex-grow container mx-auto p-4">
                <h2 className="text-3xl font-bold text-center text-blue-600 mb-6">Edit Team</h2>
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-md space-y-4 max-w-md mx-auto">
                    <div>
                        <label className="block text-gray-700 font-medium">Team Name</label>
                        <input
                            type="text"
                            name="teamname"
                            value={formValues.teamname}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded p-2 mt-1 focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-medium">Organization Name</label>
                        <input
                            type="text"
                            name="teamorg"
                            value={formValues.teamorg}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded p-2 mt-1 focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 transition">
                        Save Changes
                    </button>
                </form>
                <form onSubmit={coachRequest} className="bg-white p-6 rounded-xl shadow-md space-y-4 max-w-md mx-auto">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">Team Coach</h3>
                        {coach ? (
                            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg shadow-sm">
                                <span className="text-blue-800 font-medium">{coach.name}</span>
                                <button type="button" onClick={() => handleDelete(coach.name)} className="text-red-500 hover:text-red-700">
                                    {/*<Trash2 size={18} />*/}
                                </button>
                            </div>
                        ) : (
                            <input
                                type="text"
                                name="coachName"
                                value={formValues.coachName}
                                onChange={handleChange}
                                placeholder="Enter coach username"
                                className="w-full border border-gray-300 rounded p-2 mt-1 focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                    </div>

                    <button type="submit" className={`${coach ? 'hidden' : ''} w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 transition`}>
                        Request Coach
                    </button>
                </form>
                <form className="hidden bg-white p-6 rounded-xl shadow-md space-y-4 max-w-md mx-auto">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">Team Members</h3>
                        <ul className="space-y-2">
                            {members.map((member, index) => (
                                <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg shadow-sm">
                                    <span>{member.name}</span>
                                    <button type="button" onClick={() => handleDelete(member.name)} className="text-red-500 hover:text-red-700">
                                        <Trash2 size={18} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 transition">
                        Save Changes
                    </button>
                </form>
            </div>
            <CustomFooter />
        </div>
    );
};

export default EditTeam;
